import { Button, Form } from '@weezevent/nacre';
import React from 'react';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import SessionModel from '../../../../../models/sessionModel';
import LoadingCard from '../../../../../components/loadingCard';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import css from '../../styles.module.css';
import CalendarSessionsInput, { toObject, sessionIsAvailable } from './CalendarSessionsInput';
import useAppContext from '../../../../../context/useAppContext';

export const dateFormatTimezone = (newDate, event_timezone, moment) => {
    if (!newDate) {
        return null;
    }
    return moment.tz(newDate.utc().tz(event_timezone), event_timezone).format('YYYY-MM-DD');
};

export default function SessionsSelect({ onChangeSelectedSessions, selectedDay, setSelectedDay = () => {}, setDatesIds, onChangeSteps, form, loading, loadingInventories }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [{ organization, event, event_timezone }] = useAppContext();

    const [waitingForInventories, setWaitingForInventories] = React.useState(loadingInventories);

    React.useEffect(() => {
        if (!loadingInventories) {
            setWaitingForInventories(false);
        }
    }, [loadingInventories]);

    const [countSessions, setCountSessions] = React.useState(0);
    const [sessionSelected, setSessionSelected] = React.useState(null);

    const dateFormat = React.useCallback(newDate => dateFormatTimezone(newDate, event_timezone, moment), [event_timezone, moment]);

    const onSessionSelected = React.useCallback(
        session => {
            setSessionSelected(session);
            onChangeSelectedSessions(session ? [{ ...session, value: session.uuid }] : []);
        },
        [onChangeSelectedSessions]
    );

    const updateSession = React.useCallback(
        sessions => {
            const sessionsObjects = sessions?.map(toObject);
            const availableSessions = sessionsObjects?.filter(sessionIsAvailable);
            setCountSessions(availableSessions?.length);
            setDatesIds(sessionsObjects?.map(s => s.weez_date_id));

            let foundOption = null;
            if (form?.sessions?.length) {
                // when going back to the step we want to use the previously selected session
                foundOption = availableSessions?.find(s => s.uuid === form.sessions[0].uuid);
            }
            if (foundOption) {
                // already set in form.sessions (just need local state to update)
                setSessionSelected(foundOption);
            } else {
                // set first available as default selected (+form)
                const firstWithQuota = availableSessions[0];
                if (firstWithQuota) {
                    onSessionSelected(firstWithQuota);
                }
            }
        },
        [onSessionSelected, form]
    );

    const [sessionsDays, loadingSessions] = SessionModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            defaultLoading: false,
            sync: true,
            launch: Boolean(organization.id && event.id && selectedDay && (!waitingForInventories || event.isUseSessionsNoSeated())),
            query: {
                ...(selectedDay && { date: dateFormat(selectedDay) })
            },
            onSuccess: resp => updateSession(resp)
        },
        [organization, event, selectedDay, waitingForInventories]
    );

    const onDaySelected = React.useCallback(
        day => {
            if (selectedDay !== day && !loadingSessions) {
                onSessionSelected(null);
                if (!event.isUseSessionsNoSeated()) {
                    setWaitingForInventories(true);
                }
                setSelectedDay(day);
                setCountSessions(0);
            }
        },
        [onSessionSelected, setSelectedDay, selectedDay, loadingSessions, event]
    );

    const handleNext = React.useCallback(() => {
        onChangeSteps(2);
        track('ACTION > VALIDATE SESSIONS', { 'Page Title': 'ADD ATTENDEES' });
    }, [onChangeSteps, track]);

    if (loading) {
        return <LoadingCard loading />;
    }

    return (
        <>
            <Form.Field>
                <CalendarSessionsInput
                    selectLabel={t('ticket.manager.attendees.action.swap_session.availableSession', { count: countSessions })}
                    selectPlaceholder={t('ticket.manager.attendees.action.swap_session.placeholderSelect')}
                    daySelected={selectedDay}
                    onDaySelected={onDaySelected}
                    sessionSelected={sessionSelected}
                    onSessionSelected={onSessionSelected}
                    sessionsDays={sessionsDays}
                    loadingSessions={loadingSessions || waitingForInventories}
                    loading={loading}
                />
            </Form.Field>
            <Button
                className={css['progress-buttons']}
                primary
                disabled={!form.sessions?.length || (!form.inventories?.length && event.isRealSeatingWithSessions()) || waitingForInventories}
                label={t('ticket.common.action.continue')}
                onClick={handleNext}
            />
        </>
    );
}
